import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useAuth, useApi } from '@/context/AuthContext';
import { formatIndianCurrency, formatDate, GST_RATES, INDIAN_STATES, calculateGST, toLocalISODate, stateOf, getErrorMessage } from '@/utils/helpers';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { CalendarIcon, Plus, Trash2, Save, Send, ArrowLeft, Search } from 'lucide-react';

export default function InvoiceBuilderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { business } = useAuth();
  const api = useApi();
  
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hsnResults, setHsnResults] = useState([]);
  const [hsnSearching, setHsnSearching] = useState(false);
  
  const [formData, setFormData] = useState({
    customer_id: '',
    transaction_date: toLocalISODate(),
    due_date: '',
    place_of_supply: '',
    notes: '',
    is_igst: null,
    items: [
      { description: '', hsn_sac_code: '', quantity: 1, unit: 'Nos', unit_price: 0, discount_pct: 0, gst_rate: 18 }
    ]
  });

  const [selectedCustomer, setSelectedCustomer] = useState(null);

  useEffect(() => {
    fetchCustomers();
    if (id) {
      fetchInvoice();
    }
  }, [id]);

  useEffect(() => {
    if (formData.customer_id && !selectedCustomer && customers.length) {
      setSelectedCustomer(customers.find(c => c.id === formData.customer_id) || null);
    }
  }, [customers, formData.customer_id, selectedCustomer]);

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchInvoice = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/invoices/${id}`);
      const invoice = response.data;
      setFormData({
        customer_id: invoice.customer_id,
        transaction_date: invoice.transaction_date,
        due_date: invoice.due_date || '',
        place_of_supply: invoice.place_of_supply || '',
        notes: invoice.notes || '',
        is_igst: invoice.is_igst,
        items: invoice.items.map(item => ({
          description: item.description,
          hsn_sac_code: item.hsn_sac_code || '',
          quantity: item.quantity,
          unit: item.unit || 'Nos',
          unit_price: item.unit_price,
          discount_pct: item.discount_pct || 0,
          gst_rate: item.gst_rate
        }))
      });
    } catch (error) {
      toast.error('Failed to load invoice');
      navigate('/invoices');
    } finally {
      setLoading(false);
    }
  };

  const searchHSN = async (query) => {
    if (query.length < 2) {
      setHsnResults([]);
      return;
    }
    setHsnSearching(true);
    try {
      const response = await api.get(`/hsn-sac/search?query=${query}`);
      setHsnResults(response.data);
    } catch (error) {
      console.error('HSN search error:', error);
    } finally {
      setHsnSearching(false);
    }
  };

  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    setSelectedCustomer(customer);
    setFormData(prev => ({
      ...prev,
      customer_id: customerId,
      place_of_supply: stateOf(customer)
    }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { description: '', hsn_sac_code: '', quantity: 1, unit: 'Nos', unit_price: 0, discount_pct: 0, gst_rate: 18 }]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) return;
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  const calculateTotals = () => {
    const businessState = stateOf(business);
    const customerState = formData.place_of_supply || stateOf(selectedCustomer);
    const isIGST = formData.is_igst !== null && formData.is_igst !== undefined
      ? formData.is_igst
      : (!!customerState && !!businessState && businessState !== customerState);
    
    let subtotal = 0;
    let totalCGST = 0;
    let totalSGST = 0;
    let totalIGST = 0;
    
    formData.items.forEach(item => {
      const taxable = item.quantity * item.unit_price * (1 - item.discount_pct / 100);
      subtotal += taxable;
      const gst = calculateGST(taxable, item.gst_rate, isIGST);
      totalCGST += gst.cgst;
      totalSGST += gst.sgst;
      totalIGST += gst.igst;
    });
    
    const totalTax = totalCGST + totalSGST + totalIGST;
    const grandTotal = subtotal + totalTax;
    
    return { subtotal, totalCGST, totalSGST, totalIGST, totalTax, grandTotal, isIGST };
  };

  const handleSave = async (sendWhatsApp = false) => {
    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    if (!formData.items.some(item => item.description && item.unit_price > 0)) {
      toast.error('Please add at least one item');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        items: formData.items.filter(item => item.description && item.unit_price > 0)
      };
      
      let response;
      if (id) {
        response = await api.put(`/invoices/${id}`, payload);
      } else {
        response = await api.post('/invoices', payload);
      }
      
      toast.success(id ? 'Invoice updated' : 'Invoice created successfully');
      
      if (sendWhatsApp) {
        try {
          const whatsappResponse = await api.post(`/whatsapp/send-invoice/${response.data.id}`);
          toast.success(whatsappResponse.data.message);
        } catch (error) {
          toast.error(getErrorMessage(error, 'Invoice saved but failed to send via WhatsApp'));
        }
      }
      
      navigate('/invoices');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save invoice'));
    } finally {
      setSaving(false);
    }
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-stone-200 rounded w-1/3"></div>
          <div className="h-96 bg-stone-200 rounded"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="invoice-builder">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/invoices')} className="gap-2">
              <ArrowLeft className="w-4 h-4" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-stone-900 font-['Outfit']">
                {id ? 'Edit Invoice' : 'Create Invoice'}
              </h1>
              <p className="text-stone-600">GST-compliant invoice builder</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => handleSave(false)}
              disabled={saving}
              className="gap-2"
              data-testid="save-draft-btn"
            >
              <Save className="w-4 h-4" /> Save Draft
            </Button>
            <Button 
              onClick={() => handleSave(true)}
              disabled={saving}
              className="bg-[#1D9E75] hover:bg-[#178a65] text-white gap-2"
              data-testid="save-send-btn"
            >
              <Send className="w-4 h-4" /> Save & Mark Sent
            </Button>
          </div>
        </div>

        {/* Invoice Form */}
        <div className="bg-stone-200 p-8 rounded-xl">
          <div className="invoice-paper max-w-4xl mx-auto p-8">
            {/* Business Header */}
            <div className="flex justify-between items-start mb-8 pb-6 border-b border-stone-200">
              <div>
                <h2 className="text-2xl font-bold text-[#1D9E75] font-['Outfit']">{business?.name || 'Your Business'}</h2>
                {business?.gstin && (
                  <p className="text-sm text-stone-600 font-mono mt-1">GSTIN: {business.gstin}</p>
                )}
                {business?.address && (
                  <p className="text-sm text-stone-500 mt-1">
                    {business.address.line1}, {business.address.city}, {business.address.state} - {business.address.pincode}
                  </p>
                )}
              </div>
              <div className="text-right">
                <h3 className="text-3xl font-bold text-stone-900 font-['Outfit']">INVOICE</h3>
                <p className="text-stone-500 text-sm mt-1">Tax Invoice under GST</p>
              </div>
            </div>

            {/* Customer and Dates */}
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="space-y-4">
                <div>
                  <Label>Bill To *</Label>
                  <Select value={formData.customer_id} onValueChange={handleCustomerChange}>
                    <SelectTrigger data-testid="customer-select">
                      <SelectValue placeholder="Select customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map(customer => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customer.name} {customer.gstin && `(${customer.gstin})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {customers.length === 0 && (
                    <p className="text-sm text-stone-500 mt-1">
                      No customers yet. <a href="/customers" className="text-[#1D9E75]">Add one</a>
                    </p>
                  )}
                </div>
                
                {selectedCustomer && (
                  <div className="bg-stone-50 p-4 rounded-lg text-sm">
                    <p className="font-medium">{selectedCustomer.name}</p>
                    {selectedCustomer.gstin && <p className="text-stone-600 font-mono">GSTIN: {selectedCustomer.gstin}</p>}
                    {selectedCustomer.billing_address && (
                      <p className="text-stone-500 mt-1">
                        {[selectedCustomer.billing_address.line1, selectedCustomer.billing_address.city].filter(Boolean).join(', ')}
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <Label>Place of Supply</Label>
                  <Select 
                    value={formData.place_of_supply} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, place_of_supply: v }))}
                  >
                    <SelectTrigger data-testid="place-of-supply">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDIAN_STATES.map(state => (
                        <SelectItem key={state.code} value={state.code}>
                          {state.code} - {state.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Invoice Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="invoice-date">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.transaction_date ? format(new Date(formData.transaction_date), 'dd/MM/yyyy') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.transaction_date ? new Date(formData.transaction_date) : undefined}
                        onSelect={(date) => setFormData(prev => ({ 
                          ...prev, 
                          transaction_date: date ? toLocalISODate(date) : '' 
                        }))}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <Label>Due Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="due-date">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.due_date ? format(new Date(formData.due_date), 'dd/MM/yyyy') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.due_date ? new Date(formData.due_date) : undefined}
                        onSelect={(date) => setFormData(prev => ({ 
                          ...prev, 
                          due_date: date ? toLocalISODate(date) : '' 
                        }))}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="bg-[#1D9E75]/5 p-4 rounded-lg">
                  <p className="text-sm font-medium text-[#178a65]">
                    Tax Type: {totals.isIGST ? 'IGST (Interstate)' : 'CGST + SGST (Intrastate)'}
                  </p>
                </div>
              </div>
            </div>

            {/* Line Items */}
            <div className="mb-8">
              <Label className="mb-4 block">Items</Label>
              <div className="space-y-4">
                {formData.items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-3 items-end bg-stone-50 p-4 rounded-lg" data-testid={`item-row-${index}`}>
                    <div className="col-span-4">
                      <Label className="text-xs">Description</Label>
                      <Input
                        placeholder="Item description"
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        data-testid={`item-description-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">HSN/SAC</Label>
                      <Input
                        placeholder="Code"
                        value={item.hsn_sac_code}
                        onChange={(e) => {
                          updateItem(index, 'hsn_sac_code', e.target.value);
                          searchHSN(e.target.value);
                        }}
                        className="font-mono"
                      />
                    </div>
                    <div className="col-span-1">
                      <Label className="text-xs">Qty</Label>
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 1)}
                        className="font-mono"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Rate (₹)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={item.unit_price}
                        onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                        className="font-mono"
                        data-testid={`item-rate-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">GST %</Label>
                      <Select 
                        value={item.gst_rate.toString()} 
                        onValueChange={(v) => updateItem(index, 'gst_rate', parseFloat(v))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {GST_RATES.map(rate => (
                            <SelectItem key={rate} value={rate.toString()}>{rate}%</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-1 flex justify-end">
                      {formData.items.length > 1 && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeItem(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" onClick={addItem} className="mt-4 gap-2" data-testid="add-item-btn">
                <Plus className="w-4 h-4" /> Add Item
              </Button>
            </div>

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-80 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-stone-600">Subtotal</span>
                  <span className="font-mono">{formatIndianCurrency(totals.subtotal)}</span>
                </div>
                {totals.isIGST ? (
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-600">IGST</span>
                    <span className="font-mono">{formatIndianCurrency(totals.totalIGST)}</span>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-600">CGST</span>
                      <span className="font-mono">{formatIndianCurrency(totals.totalCGST)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-600">SGST</span>
                      <span className="font-mono">{formatIndianCurrency(totals.totalSGST)}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between pt-2 border-t border-stone-200 text-lg font-bold">
                  <span>Grand Total</span>
                  <span className="font-mono text-[#1D9E75]" data-testid="grand-total">{formatIndianCurrency(totals.grandTotal)}</span>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="mt-8 pt-6 border-t border-stone-200">
              <Label>Notes / Terms</Label>
              <Textarea
                placeholder="Add any notes or terms..."
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="mt-2"
                rows={3}
              />
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
